/* ==========================================================================
   HOPE BASE ADVISORY LLC — INVOICE & QUOTATION MAKER (WITH SIGNATURE & STAMP)
   ========================================================================== */

const API_ENDPOINT = '/api/invoices.php';

let lineItems = [
  { description: 'Strategic Advisory & Governance Framework', quantity: 1, rate: 2500 }
];

let activeDocType = 'quotation';
let currentDocId = null;
let autoSaveTimer = null;
let isAutoSaving = false;

export function initInvoiceMaker() {
  const modal = document.getElementById('invoice-studio-modal');
  const closeBtn = document.getElementById('invoice-studio-close');
  const docTypeBtns = document.querySelectorAll('.doc-type-btn');
  const addLineBtn = document.getElementById('inv-add-line');
  const itemsContainer = document.getElementById('inv-line-items-body');
  const printBtn = document.getElementById('inv-print-btn');
  const resetBtn = document.getElementById('inv-reset-btn');
  const shareLinkBtn = document.getElementById('inv-share-link-btn');
  const autoSaveBadge = document.getElementById('inv-autosave-badge');

  const taxInput = document.getElementById('inv-tax-rate');
  const discountInput = document.getElementById('inv-discount');

  const docNumberInput = document.getElementById('inv-doc-number');
  const clientOrgInput = document.getElementById('inv-client-org');
  const clientNameInput = document.getElementById('inv-client-name');
  const clientEmailInput = document.getElementById('inv-client-email');
  const clientAddressInput = document.getElementById('inv-client-address');
  const issueDateInput = document.getElementById('inv-issue-date');
  const dueDateInput = document.getElementById('inv-due-date');
  const notesInput = document.getElementById('inv-notes');

  // Open Event Listener from Admin Dashboard
  window.addEventListener('hba:open-invoice-editor', async (e) => {
    const detail = e.detail || {};
    updateSignatureAndStampImages();

    if (detail.newDoc) {
      currentDocId = null;
      activeDocType = detail.type || 'quotation';
      lineItems = [{ description: 'Strategic Advisory Consulting', quantity: 1, rate: 1500 }];
      clearFormInputs();
      await fetchNextSequentialDocNumber();
      setTodayDates();
      updateDocTypeButtons();
      renderLineItems();
      calculateTotals();
    } else if (detail.id) {
      loadDocFromDb(detail.id);
    }

    if (modal) {
      modal.style.display = 'flex';
      requestAnimationFrame(() => {
        modal.classList.add('active');
      });
      document.body.style.overflow = 'hidden';
    }
  });

  function closeInvoiceStudio() {
    if (!modal) return;
    modal.classList.remove('active');
    setTimeout(() => {
      modal.style.display = 'none';
    }, 200);
    document.body.style.overflow = '';
    window.dispatchEvent(new CustomEvent('hba:invoice-saved-to-db'));
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', closeInvoiceStudio);
  }

  // Backdrop click to close
  modal?.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeInvoiceStudio();
    }
  });

  // Inject active user handwritten signature and official company stamp
  async function updateSignatureAndStampImages(overrideSig = null) {
    const sigImg = document.getElementById('editor-sig-img');
    const stampImg = document.getElementById('editor-stamp-img');

    let savedSig = overrideSig || localStorage.getItem('hba_user_signature');
    if (!savedSig || savedSig.length < 200) {
      try {
        const response = await fetch('/api/settings.php?action=get_settings');
        const result = await response.json();
        if (result.success && result.data?._current_user?.signature_data) {
          savedSig = result.data._current_user.signature_data;
          localStorage.setItem('hba_user_signature', savedSig);
        }
      } catch (err) {}
    }

    // Clean up any legacy public file references in localStorage
    if (savedSig && (savedSig.includes('/images/') || savedSig.length < 200)) {
      savedSig = null;
      localStorage.removeItem('hba_user_signature');
    }
    let savedStamp = localStorage.getItem('hba_company_stamp');
    if (savedStamp && savedStamp.includes('/images/')) {
      savedStamp = null;
      localStorage.removeItem('hba_company_stamp');
    }

    if (sigImg) {
      if (savedSig && savedSig.startsWith('data:image/')) {
        sigImg.src = savedSig;
        sigImg.style.display = 'block';
      } else {
        sigImg.src = '';
        sigImg.style.display = 'none';
      }
    }

    if (stampImg) {
      if (savedStamp && savedStamp.startsWith('data:image/')) {
        stampImg.src = savedStamp;
        stampImg.style.display = 'block';
      } else {
        stampImg.src = '';
        stampImg.style.display = 'none';
      }
    }

    updateVerificationRef();
  }

  function updateVerificationRef(docNum) {
    const num = docNum || docNumberInput?.value || 'HBA-QUO-2026-0001';
    document.querySelectorAll('.doc-verify-ref').forEach(el => {
      el.textContent = num;
    });
  }

  // Toggle Document Type
  docTypeBtns.forEach(btn => {
    btn.addEventListener('click', async () => {
      docTypeBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activeDocType = btn.dataset.type;

      const docHeaderTitle = document.getElementById('inv-doc-header-type');
      if (docHeaderTitle) {
        docHeaderTitle.textContent = activeDocType === 'quotation' ? 'OFFICIAL QUOTATION' : 'INVOICE';
      }

      if (!currentDocId) {
        await fetchNextSequentialDocNumber();
      }
      triggerAutoSave();
    });
  });

  function updateDocTypeButtons() {
    docTypeBtns.forEach(b => {
      b.classList.toggle('active', b.dataset.type === activeDocType);
    });
    const docHeaderTitle = document.getElementById('inv-doc-header-type');
    if (docHeaderTitle) {
      docHeaderTitle.textContent = activeDocType === 'quotation' ? 'OFFICIAL QUOTATION' : 'INVOICE';
    }
  }

  // Fetch Next Sequential Document Number
  async function fetchNextSequentialDocNumber() {
    if (currentDocId) return;
    try {
      const response = await fetch(`${API_ENDPOINT}?action=get_next_number&type=${activeDocType}`);
      const result = await response.json();
      if (result.success && result.next_doc_number) {
        if (docNumberInput) {
          docNumberInput.value = result.next_doc_number;
          updateVerificationRef(result.next_doc_number);
        }
      }
    } catch (err) {
      const prefix = activeDocType === 'quotation' ? 'HBA-QUO' : 'HBA-INV';
      const year = new Date().getFullYear();
      const random = Math.floor(1000 + Math.random() * 9000);
      if (docNumberInput) {
        docNumberInput.value = `${prefix}-${year}-${random}`;
        updateVerificationRef(docNumberInput.value);
      }
    }
  }

  // Add Line Item
  if (addLineBtn) {
    addLineBtn.addEventListener('click', () => {
      lineItems.push({ description: '', quantity: 1, rate: 0 });
      renderLineItems();
      calculateTotals();
      triggerAutoSave();
    });
  }

  // Auto-Save & Re-Calculation Listeners
  const trackedInputs = [docNumberInput, clientOrgInput, clientNameInput, clientEmailInput, clientAddressInput, issueDateInput, dueDateInput, taxInput, discountInput, notesInput];
  trackedInputs.forEach(input => {
    if (input) {
      input.addEventListener('input', () => {
        if (input === docNumberInput) updateVerificationRef(docNumberInput.value);
        calculateTotals();
        triggerAutoSave();
      });
      input.addEventListener('change', () => {
        if (input === docNumberInput) updateVerificationRef(docNumberInput.value);
        calculateTotals();
        triggerAutoSave();
      });
    }
  });

  // Render Line Items Table Body
  function renderLineItems() {
    if (!itemsContainer) return;
    itemsContainer.innerHTML = '';

    lineItems.forEach((item, index) => {
      const row = document.createElement('tr');
      row.className = 'inv-line-row';
      const lineTotal = (item.quantity * item.rate).toFixed(2);

      row.innerHTML = `
        <td>
          <input type="text" class="inv-input desc-input" value="${escapeHtml(item.description)}" placeholder="Service / Item Description" data-index="${index}">
        </td>
        <td>
          <input type="number" class="inv-input qty-input" value="${item.quantity}" min="1" step="1" data-index="${index}">
        </td>
        <td>
          <input type="number" class="inv-input rate-input" value="${item.rate}" min="0" step="10" data-index="${index}">
        </td>
        <td class="inv-col-total">
          $${lineTotal}
        </td>
        <td class="no-print">
          <button type="button" class="inv-btn-delete" data-index="${index}" title="Remove Item">✕</button>
        </td>
      `;

      itemsContainer.appendChild(row);
    });

    itemsContainer.querySelectorAll('.desc-input').forEach(input => {
      input.addEventListener('input', (e) => {
        const idx = e.target.dataset.index;
        lineItems[idx].description = e.target.value;
        triggerAutoSave();
      });
    });

    itemsContainer.querySelectorAll('.qty-input').forEach(input => {
      input.addEventListener('input', (e) => {
        const idx = e.target.dataset.index;
        lineItems[idx].quantity = parseFloat(e.target.value) || 0;
        updateRowTotal(idx);
        calculateTotals();
        triggerAutoSave();
      });
    });

    itemsContainer.querySelectorAll('.rate-input').forEach(input => {
      input.addEventListener('input', (e) => {
        const idx = e.target.dataset.index;
        lineItems[idx].rate = parseFloat(e.target.value) || 0;
        updateRowTotal(idx);
        calculateTotals();
        triggerAutoSave();
      });
    });

    itemsContainer.querySelectorAll('.inv-btn-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const idx = e.target.dataset.index;
        if (lineItems.length > 1) {
          lineItems.splice(idx, 1);
          renderLineItems();
          calculateTotals();
          triggerAutoSave();
        }
      });
    });
  }

  function updateRowTotal(index) {
    const row = itemsContainer.children[index];
    if (row) {
      const totalCell = row.querySelector('.inv-col-total');
      const item = lineItems[index];
      if (totalCell && item) {
        totalCell.textContent = `$${(item.quantity * item.rate).toFixed(2)}`;
      }
    }
  }

  // Calculate Financial Totals
  function calculateTotals() {
    let subtotal = 0;
    lineItems.forEach(item => {
      subtotal += (item.quantity || 0) * (item.rate || 0);
    });

    const taxRate = parseFloat(taxInput?.value || 0);
    const taxAmount = subtotal * (taxRate / 100);
    const discountVal = parseFloat(discountInput?.value || 0);
    const grandTotal = Math.max(0, subtotal + taxAmount - discountVal);

    const subtotalEl = document.getElementById('inv-val-subtotal');
    const taxValEl = document.getElementById('inv-val-tax');
    const grandTotalEl = document.getElementById('inv-val-grandtotal');

    if (subtotalEl) subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
    if (taxValEl) taxValEl.textContent = `$${taxAmount.toFixed(2)}`;
    if (grandTotalEl) grandTotalEl.textContent = `$${grandTotal.toFixed(2)}`;
  }

  // Automatic Debounced Auto-Save Engine
  function triggerAutoSave() {
    if (autoSaveBadge) {
      autoSaveBadge.textContent = 'Auto-saving...';
      autoSaveBadge.className = 'pill-badge orange';
    }

    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => {
      executeAutoSave();
    }, 800);
  }

  async function executeAutoSave() {
    if (isAutoSaving) return;
    isAutoSaving = true;
    const payload = getFormPayload();

    try {
      const response = await fetch(`${API_ENDPOINT}?action=save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (result.success) {
        currentDocId = result.id;
        if (result.doc_number && docNumberInput) {
          docNumberInput.value = result.doc_number;
        }
        if (autoSaveBadge) {
          autoSaveBadge.textContent = 'Saved to Database ✓';
          autoSaveBadge.className = 'pill-badge teal';
        }
        window.dispatchEvent(new CustomEvent('hba:invoice-saved-to-db'));
      }
    } catch (err) {
      if (autoSaveBadge) {
        autoSaveBadge.textContent = 'Draft Saved';
        autoSaveBadge.className = 'pill-badge';
      }
    } finally {
      isAutoSaving = false;
    }
  }

  function showStudioToast(msg, type = 'success') {
    let t = document.getElementById('studio-toast-banner');
    if (!t) {
      t = document.createElement('div');
      t.id = 'studio-toast-banner';
      t.style.cssText = 'position:fixed; top:24px; left:50%; transform:translateX(-50%); z-index:30000; padding:12px 24px; border-radius:8px; font-size:0.875rem; font-weight:600; box-shadow:0 10px 25px rgba(0,0,0,0.3); transition:all 0.3s ease; text-align:center; pointer-events:none;';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.display = 'block';
    t.style.background = type === 'success' ? '#10B981' : '#662D91';
    t.style.color = '#FFFFFF';
    setTimeout(() => {
      t.style.display = 'none';
    }, 3500);
  }

  // Copy Shareable Link to Clipboard (No Browser Alert/Prompt Popups!)
  if (shareLinkBtn) {
    shareLinkBtn.addEventListener('click', () => {
      const docNum = docNumberInput?.value || 'HBA-QUO-2026-0001';
      const shareUrl = `${window.location.origin}/#doc/${docNum}`;
      navigator.clipboard.writeText(shareUrl).then(() => {
        showStudioToast(`Shareable Link Copied to Clipboard! ✓ (${docNum})`, 'success');
      }).catch(() => {
        showStudioToast(`Shareable Link: ${shareUrl}`, 'info');
      });
    });
  }

  // Load Document Payload from Database
  async function loadDocFromDb(id) {
    try {
      const response = await fetch(`${API_ENDPOINT}?action=get&id=${id}`);
      const result = await response.json();
      if (result.success && result.data) {
        currentDocId = id;
        activeDocType = result.data.doc_type || 'quotation';
        updateDocTypeButtons();

        const docData = result.data.doc_data || {};
        populateFormPayload(result.data, docData);

        if (autoSaveBadge) {
          autoSaveBadge.textContent = 'Saved in Database ✓';
          autoSaveBadge.className = 'pill-badge teal';
        }
      }
    } catch (err) {
      console.error('Error loading document:', err);
    }
  }

  function getFormPayload() {
    let subtotal = 0;
    lineItems.forEach(item => subtotal += item.quantity * item.rate);
    const taxRate = parseFloat(taxInput?.value || 0);
    const taxAmount = subtotal * (taxRate / 100);
    const discountVal = parseFloat(discountInput?.value || 0);
    const grandTotal = Math.max(0, subtotal + taxAmount - discountVal);

    return {
      id: currentDocId,
      doc_number: docNumberInput?.value || '',
      doc_type: activeDocType,
      client_name: clientNameInput?.value || '',
      client_org: clientOrgInput?.value || '',
      client_email: clientEmailInput?.value || '',
      client_address: clientAddressInput?.value || '',
      issue_date: issueDateInput?.value || '',
      due_date: dueDateInput?.value || '',
      total_amount: grandTotal,
      doc_data: {
        lineItems: lineItems,
        taxRate: taxRate,
        discountVal: discountVal,
        notes: notesInput?.value || '',
        signature_data: localStorage.getItem('hba_user_signature') || ''
      }
    };
  }

  function populateFormPayload(record, innerData) {
    if (docNumberInput) {
      docNumberInput.value = record.doc_number || '';
      updateVerificationRef(record.doc_number);
    }
    if (clientOrgInput) clientOrgInput.value = record.client_org || record.client_name || '';
    if (clientNameInput) clientNameInput.value = record.client_name || '';
    if (clientEmailInput) clientEmailInput.value = record.client_email || '';
    if (clientAddressInput) clientAddressInput.value = record.client_address || '';
    if (issueDateInput) issueDateInput.value = record.issue_date || '';
    if (dueDateInput) dueDateInput.value = record.due_date || '';

    if (innerData.taxRate !== undefined && taxInput) taxInput.value = innerData.taxRate;
    if (innerData.discountVal !== undefined && discountInput) discountInput.value = innerData.discountVal;
    if (innerData.notes !== undefined && notesInput) notesInput.value = innerData.notes;

    if (innerData.lineItems && Array.isArray(innerData.lineItems) && innerData.lineItems.length > 0) {
      lineItems = innerData.lineItems.map(item => ({
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 1,
        rate: parseFloat(item.rate) || 0
      }));
    } else {
      const tot = parseFloat(record.total_amount) || 1500;
      lineItems = [{ description: 'Strategic Advisory & Governance Framework', quantity: 1, rate: tot }];
    }
    renderLineItems();
    calculateTotals();
    updateSignatureAndStampImages(innerData.signature_data || record.signature_data);
  }

  function clearFormInputs() {
    if (clientOrgInput) clientOrgInput.value = '';
    if (clientNameInput) clientNameInput.value = '';
    if (clientEmailInput) clientEmailInput.value = '';
    if (clientAddressInput) clientAddressInput.value = '';
    if (notesInput) notesInput.value = '';
    if (taxInput) taxInput.value = '0';
    if (discountInput) discountInput.value = '0';
  }

  function setTodayDates() {
    const today = new Date().toISOString().split('T')[0];
    const in30 = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    if (issueDateInput) issueDateInput.value = today;
    if (dueDateInput) dueDateInput.value = in30;
  }

  if (printBtn) {
    printBtn.addEventListener('click', () => {
      window.print();
    });
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', async () => {
      currentDocId = null;
      lineItems = [{ description: 'Strategic Advisory Consulting', quantity: 1, rate: 1500 }];
      clearFormInputs();
      await fetchNextSequentialDocNumber();
      setTodayDates();
      renderLineItems();
      calculateTotals();
      showStudioToast('New Blank Document Draft Created ✓', 'success');
    });
  }
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]));
}
